import { useState, useEffect } from 'react';
import { contentfulClient } from '../lib/contentful';
import { Entry, EntryCollection } from 'contentful';

// Generic hook for fetching any content type
export function useContentful(
  contentType: string,
  query?: any
) {
  const [data, setData] = useState<Entry<any>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await contentfulClient.getEntries({
          content_type: contentType,
          ...query
        });
        setData(response.items);
        setError(null);
      } catch (err) {
        console.error(`Error fetching ${contentType}:`, err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [contentType, JSON.stringify(query)]);

  return { data, loading, error };
}

// Specific hook for page content
export function usePageContent(sectionName: string) {
  return useContentful('pLrMSqiwtn4uSJcbEeq5j', {
    'fields.sectionName': sectionName,
    limit: 1
  });
}

// Hook for site settings
export function useSiteSettings() {
  return useContentful('4D2Uu93HnoICTwfdCEn3e4', {
    limit: 1
  });
}

// Hook for navigation - now pulled from siteSettings.globalCTAs.navigation
export function useNavigation() {
  const { data: siteSettings } = useSiteSettings();
  
  // Transform site settings navigation data to match expected format
  const globalCTAs = siteSettings?.[0]?.fields?.globalCTAs as any;
  const navigationData = globalCTAs?.navigation;
  
  return {
    data: navigationData ? [{ fields: navigationData }] : [],
    loading: !siteSettings,
    error: null
  };
}

// Hook for property page content
export function usePropertyPage(slug: string) {
  return useContentful('6EIJUgBczByvPIxCcdf9QY', {
    'fields.slug': slug,
    limit: 1
  });
}

// Hook for blog posts
export function useBlogPosts(limit?: number) {
  return useContentful('5MJkjubF2KWZzGT1ExGwsB', {
    order: '-fields.date',
    limit: limit || 10
  });
}

// Hook for single blog post by slug
export function useBlogPost(slug: string) {
  return useContentful('5MJkjubF2KWZzGT1ExGwsB', {
    'fields.slug': slug,
    limit: 1
  });
}

// Hook for packages
export function usePackages() {
  return useContentful('2EShJcGIjcbpCSb6BEw58Y', {
    // Remove order field as it doesn't exist
  });
}

// Hook for art immersions
export function useArtImmersions() {
  return useContentful('14EKp6I8UdAVBj6FFGdGra', {
    order: '-fields.date'
  });
}

// Hook for destinations
export function useDestinations() {
  return useContentful('1Onm0teAYRxStVi05sDAwz', {
    // Remove order field as it doesn't exist
  });
}

// Hook for fetching language options
export function useLanguageOptions() {
  return useContentful('qLP0VRjmu4gcgsO4fvAh9', {
    'fields.isActive': true
    // Remove order field as it doesn't exist
  });
}

// Hook for fetching resort destinations (for navigation)
export function useResortDestinations() {
  return useContentful('3UoeXCFZHETojdSCI03Fh4', {
    // Remove order field as it doesn't exist
  });
}

// Hook for fetching gallery images with alt text
export function useGalleryImages() {
  return useContentful('6xjVTiKTG2p9rrYbU3s9rV', {
    // Remove order field as it doesn't exist
  });
}

// Hook for single entry by ID
export function useContentfulEntry(entryId: string) {
  const [data, setData] = useState<Entry<any> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchEntry = async () => {
      try {
        setLoading(true);
        const response = await contentfulClient.getEntry(entryId);
        setData(response);
        setError(null);
      } catch (err) {
        console.error(`Error fetching entry ${entryId}:`, err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    if (entryId) {
      fetchEntry();
    }
  }, [entryId]);

  return { data, loading, error };
}

// Hook for fetching images by reference key
export function useContentfulImage(key: string) {
  // The image content type doesn't have a 'key' field, so return empty
  return { data: [], loading: false, error: null };
}

// Hook for fetching multiple images by keys
export function useContentfulImages(keys: string[]) {
  // The image content type doesn't have a 'key' field, so return empty
  return { data: [], loading: false, error: null };
}

// Hook for fetching image collections
export function useImageCollection(identifier: string) {
  return useContentful('3j3Io0yJi6nL2AfgqIhha8', {
    'fields.identifier': identifier,
    limit: 1,
    include: 2 // Include linked images
  });
}

// Hook for fetching hero sections
export function useHeroSection(identifier: string) {
  return useContentful('6wbmJfghdhtq7KYP4QQZ7e', {
    'fields.identifier': identifier,
    limit: 1,
    include: 2 // Include linked images
  });
}